let pdfjsPromise: Promise<any> | null = null;
let pdfjsWorkerPromise: Promise<any> | null = null;

const TYPED_ARRAY_CTOR_NAMES = [
  "Int8Array",
  "Uint8Array",
  "Uint8ClampedArray",
  "Int16Array",
  "Uint16Array",
  "Int32Array",
  "Uint32Array",
  "Float32Array",
  "Float64Array",
  "BigInt64Array",
  "BigUint64Array",
] as const;

type PdfJsWorkerModule = {
  WorkerMessageHandler?: unknown;
  default?: {
    WorkerMessageHandler?: unknown;
  };
  "pdfjs-dist/build/pdf.worker"?: {
    WorkerMessageHandler?: unknown;
  };
};

type PdfJsWorkerGlobalTarget = {
  Array?: {
    prototype?: Record<string, unknown>;
  };
  String?: {
    prototype?: Record<string, unknown>;
  };
  pdfjsWorker?: {
    WorkerMessageHandler?: unknown;
  };
  [key: string]: unknown;
};

function createAtPolyfill() {
  return function at(this: { length: number; [index: number]: unknown }, index: number) {
    const length = Number(this.length) || 0;
    if (length <= 0) {
      return undefined;
    }

    const normalizedIndex = Math.trunc(index) || 0;
    const resolvedIndex =
      normalizedIndex >= 0 ? normalizedIndex : length + normalizedIndex;

    if (resolvedIndex < 0 || resolvedIndex >= length) {
      return undefined;
    }

    return this[resolvedIndex];
  };
}

function installAtPolyfill(prototype?: Record<string, unknown>) {
  if (!prototype || typeof prototype.at === "function") {
    return false;
  }

  Object.defineProperty(prototype, "at", {
    value: createAtPolyfill(),
    configurable: true,
    writable: true,
  });

  return true;
}

export function buildPdfAssetsBaseUrl(baseUrl = "/") {
  return `${baseUrl.replace(/\/?$/, "/")}assets/`;
}

export function getPdfAssetsBaseUrl() {
  const baseUrl = import.meta.env.BASE_URL || "/";
  return buildPdfAssetsBaseUrl(baseUrl);
}

export function buildPdfWorkerSrc(baseUrl = "/") {
  return `${buildPdfAssetsBaseUrl(baseUrl)}pdf.worker.min.js`;
}

export function ensureLegacyAtSupport(
  target: PdfJsWorkerGlobalTarget = globalThis as PdfJsWorkerGlobalTarget,
) {
  let patched = false;

  patched = installAtPolyfill(target.Array?.prototype) || patched;
  patched = installAtPolyfill(target.String?.prototype) || patched;

  for (const ctorName of TYPED_ARRAY_CTOR_NAMES) {
    const ctor = target[ctorName] as { prototype?: Record<string, unknown> } | undefined;
    patched = installAtPolyfill(ctor?.prototype) || patched;
  }

  return patched;
}

export async function loadPdfJsLegacy() {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist/legacy/build/pdf");
  }
  return pdfjsPromise;
}

export async function loadPdfJsLegacyWorker() {
  if (!pdfjsWorkerPromise) {
    pdfjsWorkerPromise = import("pdfjs-dist/legacy/build/pdf.worker.min.js");
  }
  return pdfjsWorkerPromise;
}

export function resolveWorkerMessageHandler(workerModule?: PdfJsWorkerModule | null) {
  return (
    workerModule?.WorkerMessageHandler ||
    workerModule?.default?.WorkerMessageHandler ||
    workerModule?.["pdfjs-dist/build/pdf.worker"]?.WorkerMessageHandler ||
    null
  );
}

export function installPdfJsWorkerGlobal(
  workerModule?: PdfJsWorkerModule | null,
  target: PdfJsWorkerGlobalTarget = globalThis as PdfJsWorkerGlobalTarget,
) {
  const workerMessageHandler = resolveWorkerMessageHandler(workerModule);

  if (!workerMessageHandler) {
    return false;
  }

  target.pdfjsWorker = {
    ...(target.pdfjsWorker || {}),
    WorkerMessageHandler: workerMessageHandler,
  };

  return true;
}

export function resolvePdfWorkerSrc() {
  return buildPdfWorkerSrc(import.meta.env.BASE_URL || "/");
}
